import { useCallback } from 'react';
import processCollateralModal, {
	isCollateralModal,
} from 'src/components/Modals/hooks/utils/collateralModalProcessor';
import { ModalParams, ModalType } from 'src/components/Modals/types';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useVelocityAccountsStore from 'src/stores/useVelocityAccountsStore';

export interface UseModal {
	/** Current modal on stack */
	currentModal: ModalParams | undefined;
	/** Hide the current modal / clear the modal stack */
	hide(): void;
	/** Remove a modal from the stack */
	remove(modalName: ModalType): void;
	/** Show a modal without pushing it to the stack, clear the modal stack */
	show(modal: ModalParams): void;
	/** Push to the modal stack */
	push(modal: ModalParams): void;
	/** Go back to the previous modal in the stack, dismiss if no previousmodal */
	goBack: () => void;
	/** Whether there is a previous modal in the stack to go back to */
	canGoBack: boolean;
}

export default function useModal(): UseModal {
	const getAccountsStore = useVelocityAccountsStore((s) => s.get);
	const currentAccountKey = useVelocityAccountsStore((s) => s.currentUserKey);
	const isPrivyNativeAuth = useVelocityStore((s) => s.wallet.isPrivyNativeAuth);
	const modalHead = useVelocityStore((s) => s.modals);
	const set = useVelocityStore((s) => s.set);

	const hide = useCallback(() => {
		set((s) => {
			s.modals = null;
		});
	}, [set]);

	const remove = useCallback(
		(modalName: ModalType) => {
			set((s) => {
				// Remove specific modal from the modal stack
				let currentNode = s.modals;
				let parentNode: typeof s.modals = null;

				// Traverse the stack from top to bottom to find the modal to remove
				while (currentNode) {
					if (currentNode.currentModal.type === modalName) {
						// Found the modal to remove
						if (parentNode) {
							// Not the top of stack, link parent to the node below current
							parentNode.prevModalNode = currentNode.prevModalNode;
						} else {
							// Top of stack, update the stack head to the node below
							s.modals = currentNode.prevModalNode;
						}
						break;
					}
					parentNode = currentNode;
					currentNode = currentNode.prevModalNode;
				}
			});
		},
		[set]
	);

	// Some modals need to have extra logic applied before showing them.
	// These cases should be handled by extending the below function.
	const processModal = useCallback(
		(modal: ModalParams) => {
			if (isCollateralModal(modal)) {
				return processCollateralModal(
					modal,
					getAccountsStore().accounts,
					currentAccountKey,
					isPrivyNativeAuth
				);
			}
			return modal;
		},
		[currentAccountKey, getAccountsStore, isPrivyNativeAuth]
	);

	const show = useCallback(
		(newModal: ModalParams) => {
			set((s) => {
				s.modals = {
					currentModal: processModal(newModal),
					prevModalNode: null,
				};
			});
		},
		[set, processModal]
	);

	const push = useCallback(
		(newModal: ModalParams) => {
			set((s) => {
				s.modals = {
					currentModal: processModal(newModal),
					prevModalNode: s.modals,
				};
			});
		},
		[set, processModal]
	);

	const goBack = useCallback(() => {
		set((s) => {
			s.modals = s.modals?.prevModalNode ?? null;
		});
	}, [set]);

	const canGoBack = !!modalHead?.prevModalNode;

	return {
		currentModal: modalHead?.currentModal,
		hide,
		show,
		push,
		remove,
		goBack,
		canGoBack,
	};
}
